import React from 'react';
import PropTypes from 'prop-types';
import Heading from '../Heading';

interface TimelineItemProps extends React.HTMLAttributes<HTMLElement> {
    marker?: string;
    title?: string;
    noExpand?: boolean;
    // Baseline arguments
    children?: React.ReactNode;
    className?: string;
}

/**
 * Individual item for a `Timeline` wrapper. Contains a numerical marker and an expandable area.
 * 
 * ## _
 * ### Arguments
 * @param {string} marker - A numerical value for the step in which this item is. Could be controlled by an index of a set of items, but can also be replaced with text.
 * @param {string} title - The text that is shown always, even before the panel has been expanded.
 * @param {boolean} noExpand - A flag that disables the expanding feature.
 * @param {React.ReactNode} children - The contents inside the timeline item.
 * @param {string} className - CSS classes to apply to the timeline wrapper.
 */
class TimelineItem extends React.Component<TimelineItemProps> {
    state = {
        isExpanded: false
    }

    static propTypes = {
        marker: PropTypes.string,
        title: PropTypes.string,
        noExpand: PropTypes.bool,
        children: PropTypes.node,
        className: PropTypes.string,
    }

    static defaultProps = {
        marker: "",
        title: "",
        noExpand: false,
        className: "",
    }

    handleToggleExpand = () => {
        if(!this.props.noExpand){
            this.setState({...this.state, isExpanded: !this.state.isExpanded})
        }
    }

    render() {
        const { marker, title, noExpand, children, className, ...rest } = this.props;

        let classes = `o-timeline__item`
        if (className) classes += ` ${className}`

        return (
            <li className={classes} {...rest}>
                <div className="o-timeline__marker">{marker}</div>
                <div className={`m-collapsible-panel m-collapsible-panel--shaded o-timeline__content ${this.state.isExpanded ? "m-collapsible-panel--expanded" : ""}`}>
                    <button className="m-collapsible-panel__heading" aria-expanded={this.state.isExpanded} onClick={this.handleToggleExpand}>
                        <Heading size={5}>{title}</Heading>
                        {!noExpand && 
                            <span className="m-collapsible-panel__collapse-toggle">
                                <svg className="m-collapsible-panel__collapse-icon" xmlns="http://www.w3.org/2000/svg" width="15.08" height="15.08" viewBox="0 0 15.08 15.08" aria-hidden="true">
                                    <g id="Layer_2" data-name="Layer 2" transform="translate(-6.46 -6.46)">
                                        <g id="Layer_1" data-name="Layer 1">
                                            <rect className="m-collapsible-panel__collapse-icon-horizontal" id="Rectangle_45" data-name="Rectangle 45" width="15.08" height="2.15" transform="translate(6.46 12.92)"></rect>
                                            <rect className="m-collapsible-panel__collapse-icon-vertical" id="Rectangle_46" data-name="Rectangle 46" width="2.15" height="15.08" transform="translate(12.92 6.46)"></rect>
                                        </g>
                                    </g>
                                </svg>
                            </span>
                        }
                    </button>
                    {!noExpand && 
                        <div className="m-collapsible-panel__contents">
                            <div className="row">
                                <div className="col-12">
                                    {children}
                                </div>
                            </div>
                        </div>
                    }
                </div>
            </li>
        );
    }
}

export default TimelineItem;